import { applySpec, groupBy, map, pipe, values } from 'ramda';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { DeliveryLocation } from '../interfaces/DeliveryLocation';
import { google } from 'google-maps';
import { OptionType } from '../types/OptionType';
import { TRANSPORT_COUNTRIES_AS_CHUNKS } from '../constants/transportCountries';

const baseGoogleOptions: google.maps.places.AutocompleteOptions = {
  types: ['address']
};

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;
const MAX_RESULTS = 5;

type Place = google.maps.places.AutocompletePrediction;
type PlaceResult = google.maps.places.PlaceResult;

const normalizeInput = (raw: string): string => {
  if (raw.includes(',')) return raw;
  return raw.replace(/^(.+?\s\d+(?:\s?[a-z])?)\s+(\D.+)$/i, '$1, $2');
};

const toWords = (text: string): string[] =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

const countMatchedWords = (queryWords: string[], place: Place): number => {
  const placeWords = toWords(place.description);
  return queryWords.filter(queryWord =>
    placeWords.some(placeWord => placeWord.startsWith(queryWord))
  ).length;
};

const keepBestMatches = (query: string, places: Place[]): Place[] => {
  const queryWords = toWords(query);
  const scores = places.map(place => countMatchedWords(queryWords, place));
  const bestScore = Math.max(0, ...scores);
  return places.filter((_, index) => scores[index] === bestScore);
};

/* Convert the Google placeResult to something useful */
const createAddress = (
  placeResult: PlaceResult
): DeliveryLocation | undefined => {
  const grouped = pipe(
    values,
    groupBy((component: Record<string, string>) => component.types)
  )(placeResult.address_components);

  const address = applySpec({
    streetName: component => component?.route?.[0]?.long_name ?? '',
    streetNumber: component => component?.street_number?.[0]?.long_name ?? '',
    city: component =>
      component?.postal_town?.[0]?.long_name ??
      component?.['locality,political']?.[0]?.long_name ??
      '',
    zipCode: component => component?.postal_code?.[0]?.long_name ?? '',
    countryLongName: component =>
      component?.['country,political']?.[0]?.long_name ?? ''
  })(grouped);

  const country =
    grouped?.['country,political']?.[0]?.short_name?.toLowerCase() ?? 'se';

  const lat = placeResult.geometry?.location?.lat().toString();
  const lng = placeResult.geometry?.location?.lng().toString();

  if (!lat || !lng) {
    return {
      ...address,
      lat: '',
      lng: '',
      country
    };
  }

  return { ...address, lat, lng, country };
};

/**
 * @description Queries Google maps API for the input value, returns choosen address.
 * @important You need to initialize the google api too use this hook. (ex: Render <GoogleMapsApiLoader />)
 */
export const useGoogleAddress = (): {
  value: string;
  onChange: (value: string) => void;
  setOption: Dispatch<SetStateAction<OptionType | null>>;
  options: OptionType[];
  address: DeliveryLocation | null;
} => {
  const [inputValue, setInputValue] = useState('');
  const [places, setPlaces] = useState<Place[]>([]);
  const [option, setOption] = useState<OptionType | null>();
  const [placeResult, setPlaceResult] = useState<PlaceResult | null>();

  useEffect(() => {
    if (!option?.value) {
      setPlaceResult(null);
      return;
    }

    const placeId = option.value;
    let cancelled = false;
    const { google } = window;
    const service = new google.maps.places.PlacesService(
      document.createElement('div')
    );

    service.getDetails({ placeId }, (result, status) => {
      if (cancelled) {
        return;
      }
      if (
        status !== google.maps.places.PlacesServiceStatus.OK ||
        !result ||
        result.place_id !== placeId
      ) {
        return;
      }
      setPlaceResult(result);
    });

    return () => {
      cancelled = true;
    };
  }, [option?.value]);

  useEffect(() => {
    if (!inputValue || inputValue.length < MIN_QUERY_LENGTH) {
      setPlaces([]);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(() => {
      const { google } = window;
      const service = new google.maps.places.AutocompleteService();
      const query = normalizeInput(inputValue);

      const requests = TRANSPORT_COUNTRIES_AS_CHUNKS.map(
        chunk =>
          new Promise<Place[]>(resolve =>
            service.getPlacePredictions(
              {
                input: query,
                ...baseGoogleOptions,
                componentRestrictions: { country: [...chunk] }
              },
              results => resolve(results ?? [])
            )
          )
      );

      void Promise.all(requests).then(batches => {
        if (cancelled) return;
        const seen = new Set<string>();
        const merged: Place[] = [];
        for (const batch of batches) {
          for (const place of batch) {
            if (!seen.has(place.place_id)) {
              seen.add(place.place_id);
              merged.push(place);
            }
          }
        }
        setPlaces(keepBestMatches(query, merged).slice(0, MAX_RESULTS));
      });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [option, inputValue]);

  /* Apply spec to places. */
  const options = map(
    applySpec<OptionType>({
      label: (place: Place) => place.description,
      value: (place: Place) => place.place_id
    })
  )(places || []);

  const address = useMemo(
    () =>
      option?.value && placeResult && placeResult.place_id === option.value
        ? (createAddress(placeResult) ?? null)
        : null,
    [option?.value, placeResult]
  );

  return {
    options,
    setOption,
    value: inputValue,
    onChange: setInputValue,
    address
  };
};
